import { APIClient, HttpClient } from "@/utils/httpClient";
import { defineStore } from "pinia";
import { getRouter } from "@/utils/routerRef";
import { campaignStore } from "./campaignStore";
import { pendingEventStore } from "./pendingEventStore";
import { v4 as uuidv4 } from "uuid";
import { jwtDecode } from "jwt-decode";

interface IAuthStoreState {
    campaignId: string | null;
    email: string | null;
    userId: string | null;
    walletId: string | null;
    otc: string | null;
    pageAfterAuth: boolean;
    userUUID: string | null;
    sessionId: string | null;
    journeyId: string | null;
    logging: boolean | null;
    termsAcceptedAt: string | null;
    termsAcceptedVersion: string | null;
    lastKnownGps: {
        lat: number;
        lon: number;
        accuracy: number | null;
        capturedAt: string;
    } | null;
}

export const authStore = defineStore("authStore", {
    persist: true,
    state: () =>
        ({
            campaignId: null,
            email: null,
            userId: null,
            walletId: null,
            otc: null,
            pageAfterAuth: false,
            userUUID: null,
            sessionId: null,
            journeyId: null,
            logging: true,
            termsAcceptedAt: null,
            termsAcceptedVersion: null,
            lastKnownGps: null,
        } as IAuthStoreState),
    getters: {
        getCampaignID(state: IAuthStoreState): string | null {
            return state.campaignId;
        },
        getEmail(state: IAuthStoreState): string | null {
            return state.email ? state.email.toLowerCase() : null;
        },
        getWalletID(state: IAuthStoreState): string | null {
            return state.walletId;
        },
        getUserID(state: IAuthStoreState): string | null {
            return state.userId;
        },
        getPageAfterAuth(state: IAuthStoreState): boolean {
            return state.pageAfterAuth;
        },
        getOtc(state: IAuthStoreState): string | null {
            return state.otc;
        },
        getUserUUID(state: IAuthStoreState): string | null {
            return state.userUUID;
        },
        getSessionID(state: IAuthStoreState): string | null {
            return state.sessionId;
        },
        getJourneyID(state: IAuthStoreState): string | null {
            return state.journeyId;
        },
        getLogging(state: IAuthStoreState): boolean | null {
            return state.logging;
        },
        getTermsAcceptedAt(state: IAuthStoreState): string | null {
            return state.termsAcceptedAt;
        },
        getTermsAcceptedVersion(state: IAuthStoreState): string | null {
            return state.termsAcceptedVersion;
        },
        getLastKnownGps(state: IAuthStoreState) {
            return state.lastKnownGps;
        },
    },
    actions: {
        saveOTC(otc: string) {
            this.otc = otc;
        },
        saveCampaignID(campaignId: string) {
            this.campaignId = campaignId;
        },
        saveEmail(email: string) {
            this.email = email;
        },
        saveWalletID(walletId: string) {
            this.walletId = walletId;
        },
        saveUserID(userId: string) {
            this.userId = userId;
        },
        savePageAfterAuth(pageAfterAuth: boolean) {
            this.pageAfterAuth = pageAfterAuth;
        },
        saveUserUUID(userUUID: string) {
            this.userUUID = userUUID;
        },
        saveSessionID(sessionId: string) {
            this.sessionId = sessionId;
        },
        saveJourneyID(journeyId: string) {
            this.journeyId = journeyId;
        },
        saveTermsAudit(params: {
            acceptedAt: string;
            acceptedTermsVersion: string;
        }) {
            this.termsAcceptedAt = params.acceptedAt;
            this.termsAcceptedVersion = params.acceptedTermsVersion;
        },
        recordTermsAcceptance(accepted: boolean, version?: string | null) {
            if (!accepted) {
                return;
            }

            this.termsAcceptedAt = new Date().toISOString();
            this.termsAcceptedVersion =
                version ||
                (this.campaignId ? `campaign:${this.campaignId}:default-terms` : "default-terms");
        },
        saveLastKnownGps(params: {
            lat: number;
            lon: number;
            accuracy?: number | null;
        }) {
            this.lastKnownGps = {
                lat: params.lat,
                lon: params.lon,
                accuracy: params.accuracy ?? null,
                capturedAt: new Date().toISOString(),
            };
        },
        ensureCorrelationIds() {
            if (!this.sessionId) {
                this.sessionId = uuidv4();
            }
            if (!this.journeyId) {
                this.journeyId = uuidv4();
            }
        },
        startJourney() {
            this.ensureCorrelationIds();
            this.journeyId = uuidv4();
            return this.journeyId;
        },
        logout() {
            getRouter().push(`/${this.campaignId}`).catch((error) => {
                console.error(JSON.stringify(error, null, 2));
            });
            this.email = null;
            this.userId = null;
            this.walletId = null;
            this.journeyId = uuidv4();
            localStorage.clear();
        },
        saveStopLogging() {
            this.logging = false;
            const loggingInterval = setInterval(() => {
                this.logging = true;
                clearInterval(loggingInterval);
            }, 1800000);
        },
        buildCanonicalMetadata(options?: {
            includeTermsAudit?: boolean;
            includeGps?: boolean;
            path?: string | null;
        }): Record<string, unknown> {
            const metadata: Record<string, unknown> = {
                path: options?.path ?? window.location.pathname,
                referrer: document.referrer || null,
                userAgent: navigator.userAgent,
            };

            if (options?.includeTermsAudit && this.termsAcceptedAt && this.termsAcceptedVersion) {
                metadata.acceptedAt = this.termsAcceptedAt;
                metadata.acceptedTermsVersion = this.termsAcceptedVersion;
                metadata.termsAndConditionsCheck = true;
            }

            if (options?.includeGps !== false && this.lastKnownGps) {
                metadata.gps = {
                    lat: this.lastKnownGps.lat,
                    lon: this.lastKnownGps.lon,
                    accuracy: this.lastKnownGps.accuracy,
                    capturedAt: this.lastKnownGps.capturedAt,
                };
            }

            return metadata;
        },
        enrichPayloadWithCanonicalContext(
            payload: Record<string, any>,
            options?: {
                includeTermsAudit?: boolean;
                includeGps?: boolean;
                path?: string | null;
            }
        ) {
            this.ensureCorrelationIds();
            payload.requestId = payload.requestId || uuidv4();
            payload.sessionId = payload.sessionId || this.sessionId;
            payload.journeyId = payload.journeyId || this.journeyId;
            payload.userAgent = payload.userAgent || navigator.userAgent;

            const metadata = this.buildCanonicalMetadata(options);
            payload.metadata = {
                ...(payload.metadata && typeof payload.metadata === "object" ? payload.metadata : {}),
                ...metadata,
            };

            if (options?.includeTermsAudit && this.termsAcceptedAt && this.termsAcceptedVersion) {
                payload.acceptedAt = payload.acceptedAt || this.termsAcceptedAt;
                payload.acceptedTermsVersion =
                    payload.acceptedTermsVersion || this.termsAcceptedVersion;
            }

            if (options?.includeGps !== false && this.lastKnownGps) {
                payload.gps = payload.gps || {
                    lat: this.lastKnownGps.lat,
                    lon: this.lastKnownGps.lon,
                    accuracy: this.lastKnownGps.accuracy,
                };
            }

            return payload;
        },
        async dispatchGetCampaignID(subDomain: string) {
            try {
                const response = await APIClient.get(
                    `/campaigns/subdomain/${subDomain}`
                );
                if (response.success) {
                    this.campaignId = response.data.id;
                    return this.campaignId;
                } else {
                    if (response?.data?.status == "CAMPAIGN-ENDED") {
                        getRouter()
                            .replace(`/${this.campaignId}/campaign-over`)
                            .catch(() => {
                                //
                            });
                    }
                }
            } catch (error) {
                console.error(error);
                return;
            }
        },
        async registerLoginCode(obj: any): Promise<boolean> {
            this.ensureCorrelationIds();
            const endpoint = `/campaigns/${this.campaignId}/users/register`;
            const resendPin = document.getElementById("resend-pin");
            let originalResendText = "";
            if (resendPin) {
                originalResendText = resendPin!.innerText;
                resendPin!.innerText = "Sending new code";
            }

            obj.email = obj.email.toLowerCase();
            obj.email = obj.email.trim();
            try {
                obj.identifier = this.userUUID;
                this.enrichPayloadWithCanonicalContext(obj, {
                    includeTermsAudit: Boolean(obj.termsAndConditionsCheck),
                });
            } catch (error) {
                console.error("error getting userUUID");
            }

            try {
                const response = await APIClient.post(endpoint, obj);

                if (response.success) {
                    this.saveEmail(obj.email);
                    this.saveWalletID(response.data.walletId);
                    this.saveUserID(response.data.userId);
                    if (resendPin) {
                        setTimeout(() => {
                            resendPin!.innerText = originalResendText;
                        }, 1000);
                    }
                    return true;
                } else {
                    if (response?.data?.status == "USER-REGISTERED") {
                        await this.signinLoginCode({
                            email: obj.email,
                        });
                    }
                    if (response?.data?.status == "CAMPAIGN-ENDED") {
                        getRouter()
                            .replace(`/${this.campaignId}/campaign-over`)
                            .catch(() => {
                                //
                            });
                    }
                    if (response?.data?.status == "CAMPAIGN-PAUSED") {
                        getRouter()
                            .replace(`/${this.campaignId}/campaign-paused`)
                            .catch(() => {
                                //
                            });
                    }
                    return false;
                }
            } catch (error: any) {
                console.error(JSON.stringify(error, null, 2));
                if (
                    error.response?.data?.message?.toLowerCase() ==
                    "email blocked"
                ) {
                    return false;
                }
                alert("An error occured, please try again");
                console.error(error);
                return false;
            }
        },
        async signinLoginCode(obj: any): Promise<boolean> {
            this.ensureCorrelationIds();
            const endpoint = `/campaigns/${this.campaignId}/users/login/request`;
            const resendPin = document.getElementById("resend-pin");
            let originalResendText = "";
            if (resendPin && resendPin.innerText) {
                originalResendText = resendPin!.innerText;
                resendPin!.innerText = "Sending new code";
                obj.retry = true;
            }

            obj.email = obj.email.toLowerCase();
            obj.email = obj.email.trim();
            this.enrichPayloadWithCanonicalContext(obj);

            try {
                const response = await APIClient.post(endpoint, obj);

                if (response.success) {
                    this.saveEmail(obj.email);
                    this.saveWalletID(response.data.walletId);
                    this.saveUserID(response.data.userId);
                    if (resendPin && resendPin.innerText) {
                        setTimeout(() => {
                            resendPin!.innerText = originalResendText;
                        }, 1000);
                    }
                    getRouter()
                        .replace(`/${this.campaignId}/enter-pin`)
                        .catch(() => {
                            //
                        });
                    return true;
                } else {
                    // TODO: Alert something failed
                    if (response?.data?.status == "USER-NOT-REGISTERED") {
                        alert(
                            "You are not registered. Redirecting to to the registration page."
                        );
                        getRouter().replace(`/${this.campaignId}`).catch(() => {
                            //
                        });
                    }
                    if (response?.data?.status == "CAMPAIGN-ENDED") {
                        getRouter().replace(`/${this.campaignId}/campaign-over`);
                    }
                    if (response?.data?.status == "CAMPAIGN-PAUSED") {
                        getRouter().replace(`/${this.campaignId}/campaign-paused`);
                    }

                    return false;
                }
            } catch (error) {
                console.error(error);
                alert("An error occured while signing in, please try again.");
                return false;
            }
        },
        async login(code: number) {
            this.ensureCorrelationIds();
            const endpoint = `/campaigns/${this.campaignId}/users/login`;
            try {
                const response = await APIClient.post(endpoint, {
                    email: this.email,
                    code,
                    ...this.enrichPayloadWithCanonicalContext({}),
                });
                if (response.success) {
                    localStorage.setItem(
                        "AccessToken",
                        response.data.accessToken
                    );
                    localStorage.setItem(
                        "RefreshToken",
                        response.data.refreshToken
                    );
                    this.saveWalletID(response.data.walletId);
                    pendingEventStore().startPendingEvents();
                    return true;
                    // TODO: redirect to wallet page
                } else {
                    // TODO: Alert something failed
                    if (response?.data?.status == "CAMPAIGN-ENDED") {
                        getRouter()
                            .replace(`/${this.campaignId}/campaign-over`)
                            .catch(() => {
                                //
                            });
                    }
                    if (response?.data?.status == "CAMPAIGN-PAUSED") {
                        getRouter()
                            .replace(`/${this.campaignId}/campaign-paused`)
                            .catch(() => {
                                //
                            });
                    }
                    return false;
                }
            } catch (error) {
                console.error(error);
                return false;
            }
        },
        async checkAuthenticated(): Promise<boolean> {
            const token = localStorage.getItem("RefreshToken");
            const accessToken = localStorage.getItem("AccessToken");

            // Cross-campaign JWT contamination guard.
            //
            // Cognito PreTokenGeneration stamps `custom:campaign_id` into the
            // access token at issuance (inner-reality-backend
            // src/handlers/pre-token-generation.ts:95), so a JWT is pinned to
            // exactly one campaign for its entire lifetime. The frontend
            // stores AccessToken/RefreshToken in localStorage keyed by
            // hostname — NOT by campaign — so the same token is silently
            // reused when the user navigates from campaign A to campaign B
            // on `app.prodc.innereality.com`. Backend authoriser then sees
            // `JWT.custom:campaign_id !== URL.campaignId` and 403s every
            // call. Symptom: stuck loading spinner, silent auth failures,
            // analytics events rejected, sometimes redirect loops.
            //
            // If the stored token's campaign claim doesn't match the active
            // campaign, treat the token as expired-for-this-campaign: clear
            // it, reset the identity fields the new campaign session will
            // re-establish, and return false so the caller routes to
            // Register and a fresh JWT is minted for THIS campaign.
            if (accessToken && this.campaignId) {
                try {
                    const decoded: any = jwtDecode(accessToken);
                    const tokenCampaignId = decoded?.["custom:campaign_id"];
                    if (
                        tokenCampaignId &&
                        tokenCampaignId !== this.campaignId
                    ) {
                        console.warn(
                            "checkAuthenticated: stored token campaign mismatch — clearing for re-auth",
                            {
                                tokenCampaignId,
                                activeCampaignId: this.campaignId,
                            }
                        );
                        localStorage.removeItem("AccessToken");
                        localStorage.removeItem("RefreshToken");
                        localStorage.removeItem("IdToken");
                        // Clear every identity-leaky pinia field, not just the
                        // tokens. Pinia is persisted across same-origin
                        // navigations, so a campaign-mismatch detected here
                        // means the user's prior-campaign session state is
                        // about to be rendered against the NEW campaign's
                        // data — exactly the auto-login-bleed Lisa hit on
                        // 2026-05-26 (IRL2-2876). Scrubbing only the tokens
                        // (the prior code path here) cleared auth but left
                        // walletId/sessionId/journeyId/terms/lastKnownGps in
                        // place, and the views read those fields and rendered
                        // as if the user were authed for the new campaign
                        // while every API call silently 401'd downstream.
                        this.walletId = null;
                        this.userId = null;
                        this.userUUID = null;
                        this.email = null;
                        this.otc = null;
                        this.pageAfterAuth = false;
                        this.sessionId = null;
                        this.journeyId = null;
                        this.termsAcceptedAt = null;
                        this.termsAcceptedVersion = null;
                        this.lastKnownGps = null;
                        return false;
                    }
                } catch (decodeErr) {
                    console.error(
                        "checkAuthenticated: failed to decode access token for campaign-mismatch guard",
                        decodeErr
                    );
                    // Fall through to existing logic — a malformed token
                    // will be caught downstream by HttpClient.checkExpired
                    // or the refresh call.
                }
            }

            if (token && !HttpClient.checkExpired(accessToken as string)) {
                try {
                    const response = await APIClient.refreshToken(token);

                    const campaign = campaignStore().campaign;
                    // const campaign: Campaign = store.getters["campaignStore/getItem"];

                    if (response.success) {
                        const prevWalletID = authStore().getWalletID;
                        console.log("prevWalletID");
                        console.log(prevWalletID);
                        console.log("walletId");
                        console.log(response.data.walletId);
                        if (
                            prevWalletID != null &&
                            prevWalletID != "" &&
                            prevWalletID != response.data.walletId
                        ) {
                            authStore().savePageAfterAuth(false);
                        }

                        let pageAfterAuth = campaign?.pageAfterAuth;
                        console.log(authStore().getPageAfterAuth);
                        if (
                            (!pageAfterAuth || pageAfterAuth == "") &&
                            !this.pageAfterAuth
                        ) {
                            pageAfterAuth = "vault";
                        }
                        this.saveWalletID(response.data.walletId);

                        // Only redirect if user is on unauthenticated pages or hasn't been redirected yet
                        const currentRouteName = getRouter().currentRoute.value.name;

                        // Pre-auth-only screens: an authed user should be moved
                        // off these to their pageAfterAuth target. Custom routes
                        // (T&Cs, how-to-play, campaign-paused, out-of-plays, and
                        // any other catalogue page served by ViewController) are
                        // intentionally NOT in this list — they are legitimate
                        // content surfaces an authed user may navigate to via
                        // direct URL, in-app footer link, or QR code, and must
                        // render in place rather than bouncing to /vault.
                        const unauthenticatedPages = [
                            "Register",
                            "SignIn",
                            "Enter Pin",
                        ];
                        const currentPath = window.location.pathname;

                        console.log(
                            "checkAuthenticated: Current route:",
                            currentRouteName,
                            "Path:",
                            currentPath
                        );
                        console.log(
                            "checkAuthenticated: pageAfterAuth flag:",
                            this.pageAfterAuth
                        );

                        if (
                            !this.pageAfterAuth &&
                            unauthenticatedPages.includes(
                                currentRouteName as string
                            )
                        ) {
                            // User is on an unauthenticated page and hasn't been redirected yet
                            this.savePageAfterAuth(true);
                            console.log(
                                "checkAuthenticated: Redirecting from unauthenticated page to:",
                                pageAfterAuth
                            );
                            getRouter()
                                .push({
                                    path: `/${this.campaignId}/${pageAfterAuth}`,
                                })
                                .catch(() => {
                                    //
                                });
                        } else {
                            console.log("currentPath", currentPath);
                            // User is on an authenticated page (like Prize) - don't redirect
                            if (currentPath == `/${this.campaignId}/autologin`) {
                                getRouter()
                                    .push({
                                        path: `/${this.campaignId}/${pageAfterAuth ? pageAfterAuth : "vault"}`,
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                            } else {
                                console.log(
                                    "checkAuthenticated: User already on authenticated page - not redirecting"
                                );
                            }
                        }
                        return true;
                    } else {
                        if (response?.data?.status == "CAMPAIGN-ENDED") {
                            getRouter()
                                .replace(`/${this.campaignId}/campaign-over`)
                                .catch(() => {
                                    //
                                });
                        }
                        if (response?.data?.status == "CAMPAIGN-PAUSED") {
                            getRouter()
                                .replace(`/${this.campaignId}/campaign-paused`)
                                .catch(() => {
                                    //
                                });
                        }
                        return false;
                    }
                } catch (error) {
                    console.error(error);
                    return false;
                }
            }
            return false;
        },
        async autoRegister(autoReg: { email: string; queryString: string }) {
            const endpoint = `/campaigns/${this.campaignId}/users/autoregister?${autoReg.queryString}`;

            try {
                const response = await APIClient.get(endpoint);
                console.log(response);
                if (response.success) {
                    this.saveEmail(autoReg.email);
                    this.saveWalletID(response.data.walletId);
                    this.saveUserID(response.data.userId);
                    getRouter()
                        .replace(`/${this.campaignId}/enter-pin`)
                        .catch(() => {
                            //
                        });
                } else {
                    if (response?.data?.status == "USER-REGISTERED") {
                        alert(
                            "You have already registered, redirecting to sign in page"
                        );
                        getRouter()
                            .replace(`/${this.campaignId}/sign-in`)
                            .catch(() => {
                                //
                            });
                    }
                    // TODO: Alert something failed
                    if (response?.data?.status == "CAMPAIGN-ENDED") {
                        getRouter()
                            .replace(`/${this.campaignId}/campaign-over`)
                            .catch(() => {
                                //
                            });
                    }
                    if (response?.data?.status == "CAMPAIGN-PAUSED") {
                        getRouter()
                            .replace(`/${this.campaignId}/campaign-paused`)
                            .catch(() => {
                                //
                            });
                    }
                    return false;
                }
            } catch (error) {
                console.error(error);
                return false;
            }
        },
        async dispatchUserEvent(event: {
            campaignId: string;
            event: string;
            eventName?: string;
            identifier: string;
            pageRoute: string;
            pageName?: string;
            pageType?: string;
            journeyStage?: string;
            journeyStep?: string;
            userId?: string;
            sessionId?: string;
            journeyId?: string;
            metadata?: Record<string, unknown>;
        }) {
            this.ensureCorrelationIds();
            const campaignId = event.campaignId || this.campaignId;
            if (!campaignId) {
                console.warn("Skipping user event because campaignId is not available yet", {
                    event: event.event,
                    pageRoute: event.pageRoute,
                });
                return false;
            }
            event.campaignId = campaignId;
            this.enrichPayloadWithCanonicalContext(event, {
                path: event.pageRoute,
            });
            const isAuthenticated = Boolean(this.userId && localStorage.getItem("AccessToken"));
            const endpoint = isAuthenticated ? `/events/authenticated` : `/events`;
            try {
                const response = await APIClient.post(endpoint, event);
                if (response.success) {
                    return true;
                } else {
                    return false;
                }
            } catch (error) {
                console.error(error);
                return false;
            }
        },
    },
});
